import React, { useMemo, useState } from "react";
import Button from "../includes/button";
import { TEAM_LABELS, useJobRoles } from "./hygraph";
import { BriefcaseBusiness, CheckCircle2, Clock3, MapPin, Search } from "./icons";
import TeamFilter from "./teamFilter";

const ALL_TEAMS = "All teams";
// Every team is listed, not only the ones hiring today, so the dropdown does not
// change shape as roles come and go.
const TEAM_OPTIONS = [ALL_TEAMS, ...Object.values(TEAM_LABELS)];

const RoleMeta = ({ icon: Icon, children }) => (
    <span className="flex items-center gap-[7px]">
        <Icon aria-hidden="true" focusable="false" />
        <span className="text-sm text-grey45">{children}</span>
    </span>
);

const Notice = ({ children }) => <p className="text-base text-grey45">{children}</p>;

const OpenRoles = () => {
    const { roles, loading, error, retry } = useJobRoles();
    const [query, setQuery] = useState("");
    const [team, setTeam] = useState(ALL_TEAMS);

    const visibleRoles = useMemo(() => {
        if (!roles) return [];
        const needle = query.trim().toLowerCase();
        return roles.filter((role) => {
            const matchesTeam = team === ALL_TEAMS || role.teamLabel === team;
            const matchesQuery =
                !needle ||
                role.title.toLowerCase().includes(needle) ||
                (role.summary || "").toLowerCase().includes(needle) ||
                role.teamLabel.toLowerCase().includes(needle);
            return matchesTeam && matchesQuery;
        });
    }, [roles, query, team]);

    const hasRoles = Boolean(roles && roles.length);

    return (
        <section id="open-roles" className="w-full bg-[#F8F8F8] scroll-mt-28">
            <div className="container mx-auto px-6 md:px-12 lg:px-20 pt-16 lg:pt-24 pb-24 lg:pb-36 flex flex-col gap-10 lg:gap-14 text-left">
                <h2 className="text-3xl lg:text-5xl font-bold leading-[1.1] text-blue10">Find where you fit.</h2>

                {/* Filters only earn their space once there is something to filter. */}
                {hasRoles && (
                    <div className="w-full bg-white border border-solid border-FGNeutralAccent rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                        <label className="flex-1 md:flex-none md:w-[500px] h-12 flex items-center gap-2.5 px-4 bg-white border border-solid border-FGNeutralAccent rounded-lg">
                            <Search aria-hidden="true" focusable="false" />
                            <span className="sr-only">Search roles by title or keyword</span>
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by title or keyword"
                                className="flex-1 min-w-0 bg-transparent text-sm text-blue10 placeholder:text-[#6B6F7A] outline-none border-0 p-0 focus:ring-0"
                            />
                        </label>

                        <span className="sr-only" id="team-filter-label">Filter roles by team</span>
                        <TeamFilter options={TEAM_OPTIONS} value={team} onChange={setTeam} labelId="team-filter-label" />

                        <p aria-live="polite" className="sr-only">
                            {visibleRoles.length} open {visibleRoles.length === 1 ? "role" : "roles"}
                        </p>
                    </div>
                )}

                {loading && <Notice>Loading open roles…</Notice>}

                {/* A failed request is not the same as having no openings, so it
                    says so rather than rendering an empty page. */}
                {error && !loading && (
                    <div className="flex flex-col items-start gap-4">
                        <Notice>
                            We couldn’t load our open roles just now. Please try again, or get in touch and we’ll send
                            them over.
                        </Notice>
                        <button
                            type="button"
                            onClick={retry}
                            className="text-sm font-semibold text-primaryBrand underline"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {!loading && !error && !hasRoles && (
                    <Notice>
                        We don’t have any openings posted right now. We’re always glad to hear from people who would be
                        a good fit, so please get in touch.
                    </Notice>
                )}

                {hasRoles && visibleRoles.length === 0 && (
                    <Notice>No roles match that search right now. Try a different keyword or choose All teams.</Notice>
                )}

                {visibleRoles.length > 0 && (
                    <ul className="flex flex-col gap-5 p-0 m-0">
                        {visibleRoles.map((role) => (
                            <li
                                key={role.id}
                                className="bg-white border border-solid border-FGNeutralAccent rounded-2xl p-6 lg:p-8 flex flex-col gap-6 shadow-[0px_12px_36px_0px_rgba(15,20,36,0.05)]"
                            >
                                <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
                                    <div className="flex-1 min-w-0 flex flex-col gap-3">
                                        <span className="self-start bg-[#EEF1F6] rounded-full px-2.5 py-1.5 text-xs font-bold text-primaryBrand">
                                            {role.teamLabel}
                                        </span>
                                        <h3 className="text-2xl lg:text-[28px] font-bold leading-[1.2] text-blue10">
                                            {role.title}
                                        </h3>
                                    </div>
                                    <Button
                                        to={`/careers/${role.slug}`}
                                        label="Apply Now"
                                        variant="light-primary"
                                        isHashLink={false}
                                        className="md:!w-[200px] md:shrink-0"
                                    />
                                </div>

                                <div className="flex flex-wrap gap-x-6 gap-y-3">
                                    <RoleMeta icon={MapPin}>{role.listingMeta}</RoleMeta>
                                    <RoleMeta icon={Clock3}>{role.employment}</RoleMeta>
                                    <RoleMeta icon={BriefcaseBusiness}>{role.teamLabel}</RoleMeta>
                                </div>

                                <p className="text-base leading-[1.5] text-grey45">{role.summary}</p>

                                {role.highlights.length > 0 && (
                                    <ul className="flex flex-wrap gap-x-6 gap-y-3 p-0 m-0">
                                        {role.highlights.map((highlight) => (
                                            <li key={highlight} className="flex items-center gap-2">
                                                <CheckCircle2 aria-hidden="true" focusable="false" />
                                                <span className="text-sm text-grey45">{highlight}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
};

export default OpenRoles;
